/**
 * Available deployment regions
 */
export interface RegionInfo {
  value: string;
  label: string;
  flag: string;
  currency: 'USD' | 'VND' | 'EUR';
  group: string;
  available: boolean;
  isDefault: boolean;
}

export const AVAILABLE_REGIONS: readonly RegionInfo[] = [
  // Vietnam Market (VND)
  {
    value: 'sg-1',
    label: 'Singapore',
    flag: '🇸🇬',
    currency: 'VND',
    group: 'Vietnam Market',
    available: false,
    isDefault: false,
  },
  {
    value: 'vn-south-1',
    label: 'Ho Chi Minh City',
    flag: '🇻🇳',
    currency: 'VND',
    group: 'Vietnam Market',
    available: true,
    isDefault: true,
  },
  {
    value: 'vn-north-1',
    label: 'Ha Noi',
    flag: '🇻🇳',
    currency: 'VND',
    group: 'Vietnam Market',
    available: false,
    isDefault: false,
  },
  {
    value: 'vn-central-1',
    label: 'Da Nang',
    flag: '🇻🇳',
    currency: 'VND',
    group: 'Vietnam Market',
    available: false,
    isDefault: false,
  },

  // US Market (USD)
  {
    value: 'us-east-1',
    label: 'US East (N. Virginia)',
    flag: '🇺🇸',
    currency: 'USD',
    group: 'United States',
    available: false,
    isDefault: false,
  },
  {
    value: 'us-west-2',
    label: 'US West (Oregon)',
    flag: '🇺🇸',
    currency: 'USD',
    group: 'United States',
    available: false,
    isDefault: false,
  },

  // EU Market (EUR)
  {
    value: 'eu-central-1',
    label: 'EU (Frankfurt)',
    flag: '🇪🇺',
    currency: 'EUR',
    group: 'Europe',
    available: false,
    isDefault: false,
  },
] as const;

/**
 * Get region info by code
 */
export function getRegionInfo(code: string): RegionInfo | undefined {
  return AVAILABLE_REGIONS.find(r => r.value === code);
}

/**
 * Get currency for a region
 */
export function getRegionCurrency(code: string): 'USD' | 'VND' | 'EUR' {
  return getRegionInfo(code)?.currency ?? 'VND';
}

/**
 * Get regions grouped by market
 */
export function getRegionsByGroup(): Record<string, RegionInfo[]> {
  return AVAILABLE_REGIONS.reduce((acc, region) => {
    if (!acc[region.group]) {
      acc[region.group] = [];
    }
    acc[region.group].push(region);
    return acc;
  }, {} as Record<string, RegionInfo[]>);
}

const cachedAvailableRegions = AVAILABLE_REGIONS.filter(r => r.available);
export function getAvailableRegions(): RegionInfo[] {
  return cachedAvailableRegions;
}

export function getDefaultRegion(): string {
  const defaultRegion = AVAILABLE_REGIONS.find(r => r.isDefault && r.available);
  return defaultRegion?.value || 'vn-south-1';
}

/**
 * i18n translation keys for regions
 */
export const regionI18nKeys = {
  'sg-1': 'resources:regions.sgp1',
  'vn-south-1': 'resources:regions.hcm1',
  'vn-north-1': 'resources:regions.hn1',
  'vn-central-1': 'resources:regions.dn1',
  'us-east-1': 'resources:regions.usEast1',
  'us-west-2': 'resources:regions.usWest1',
  'eu-central-1': 'resources:regions.euFrankfurt',
} as const;
